/**
 * Drive "My shares" side page.
 */

import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import {
  createDriveI18n,
  deleteJson,
  fetchJson,
  formatDate,
  jsonUrl,
  timeAgo
} from './side-utils.js'
import { DriveSideChrome } from './DriveSideChrome.jsx'

export function SharesPage() {
  const i18n = createDriveI18n()
  const t = (key) => i18n.t(key)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await fetchJson(jsonUrl()))
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function deleteShare(share) {
    if (!window.confirm(t('drive.confirm_delete_share'))) return
    setBusy(true)
    try {
      await deleteJson(share.delete_url)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  function copyLink(url) {
    if (!url) return
    navigator.clipboard?.writeText(url).catch(() => {})
  }

  if (error && !data) {
    return (
      <DriveSideChrome titleKey="drive.my_shares" titleIcon="bi-share">
        <div class="alert alert-danger">{error}</div>
      </DriveSideChrome>
    )
  }
  if (!data) {
    return (
      <DriveSideChrome titleKey="drive.my_shares" titleIcon="bi-share">
        <div class="text-muted py-4 text-center">…</div>
      </DriveSideChrome>
    )
  }

  const shares = data.shares || []

  return (
    <DriveSideChrome titleKey="drive.my_shares" titleIcon="bi-share">
    <div class="drive-container">
      {error && <div class="alert alert-warning">{error}</div>}
      {shares.length === 0 ? (
        <div class="empty-state text-center py-5">
          <i class="bi bi-share fs-1 text-muted mb-3" />
          <h5 class="text-muted">{t('drive.no_shares')}</h5>
          <p class="text-muted">{t('drive.no_shares_hint')}</p>
        </div>
      ) : (
        <div class="table-responsive">
          <table class="table table-hover">
            <thead class="d-none d-sm-table-header-group">
              <tr>
                <th>{t('drive.file')}</th>
                <th class="d-none d-md-table-cell">{t('drive.share_link')}</th>
                <th>{t('drive.created_at')}</th>
                <th class="d-none d-md-table-cell">{t('drive.expires_at')}</th>
                <th class="d-none d-md-table-cell">{t('drive.access_count')}</th>
                <th class="d-none d-lg-table-cell">{t('drive.last_accessed')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => (
                <tr key={share.id}>
                  <td>
                    <div class="d-flex align-items-center">
                      <i class={`bi ${share.icon_class || 'bi-file-earmark'} fs-5 me-2`} />
                      <div>
                        <div class="text-wrap text-break">{share.name}</div>
                        <small class="text-muted d-block d-sm-none">{formatDate(share.created_at)}</small>
                      </div>
                    </div>
                  </td>
                  <td class="d-none d-md-table-cell">
                    {share.public_url ? (
                      <div class="input-group input-group-sm">
                        <input
                          type="text"
                          class="form-control"
                          value={share.public_url}
                          readonly
                          style="max-width: 250px;"
                        />
                        <button
                          class="btn btn-outline-secondary"
                          type="button"
                          onClick={() => copyLink(share.public_url)}
                        >
                          <i class="bi bi-clipboard" />
                        </button>
                      </div>
                    ) : (
                      <span class="text-muted">—</span>
                    )}
                  </td>
                  <td class="d-none d-sm-table-cell">{formatDate(share.created_at)}</td>
                  <td class="d-none d-md-table-cell">
                    {share.expires_at ? (
                      <>
                        {formatDate(share.expires_at)}
                        {share.expired && (
                          <span class="badge bg-danger d-block mt-1">{t('drive.expired')}</span>
                        )}
                      </>
                    ) : (
                      <span class="badge bg-success">{t('drive.permanent')}</span>
                    )}
                  </td>
                  <td class="d-none d-md-table-cell">
                    <span class="badge bg-info">{share.access_count}</span>
                  </td>
                  <td class="d-none d-lg-table-cell">
                    {share.last_accessed_at ? (
                      <small>
                        {timeAgo(share.last_accessed_at, t('drive.ago'))}
                      </small>
                    ) : (
                      <span class="text-muted">
                        <small>{t('drive.never')}</small>
                      </span>
                    )}
                  </td>
                  <td>
                    <div class="btn-group btn-group-sm" role="group">
                      {share.public_url && (
                        <a
                          href={share.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="btn btn-outline-primary"
                          title={t('drive.view')}
                        >
                          <i class="bi bi-box-arrow-up-right" />
                        </a>
                      )}
                      {share.public_url && (
                        <button
                          class="btn btn-outline-secondary d-md-none"
                          type="button"
                          onClick={() => copyLink(share.public_url)}
                          title={t('drive.copy_link')}
                        >
                          <i class="bi bi-clipboard" />
                        </button>
                      )}
                      <button
                        type="button"
                        class="btn btn-outline-danger"
                        disabled={busy}
                        title={t('drive.delete')}
                        onClick={() => deleteShare(share)}
                      >
                        <i class="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </DriveSideChrome>
  )
}
